import React from "react";
import { useLocation } from "react-router-dom";
import { activityApi } from "../../api/activity";
import { useProjects } from "../../context/ProjectsContext";
import { nextPanelActivityStep } from "../../utils/activityIdentity";

const activePanelVisits = new Map();
const CLOSE_DELAY_MS = 650;

function projectKeyFromPath(pathname) {
  const match = String(pathname || "").match(/^\/dashboard\/proyectos\/([^/]+)/);
  return match?.[1] || "";
}

function closeVisit(visit, immediate = false) {
  if (!visit || visit.closed) return;
  const send = () => {
    if (visit.closed || (!immediate && visit.mounts > 0)) return;
    visit.closed = true;
    activePanelVisits.delete(visit.key);
    const durationMs = Math.max(0, Date.now() - visit.startedAt);
    activityApi.trackPanel({
      action: "panel_route_exit",
      ...visit.common,
      durationMs,
      journey: { ...visit.common.journey, durationMs },
    }).catch(() => {});
  };

  if (visit.closeTimer) clearTimeout(visit.closeTimer);
  if (immediate) send();
  else visit.closeTimer = window.setTimeout(send, CLOSE_DELAY_MS);
}

export default function PanelActivityTracker() {
  const location = useLocation();
  const { activeProject } = useProjects();

  const activeProjectId = String(activeProject?.id || activeProject?._id || "");
  const activeProjectSlug = String(activeProject?.slug || "");
  const activeProjectName = String(activeProject?.name || "");

  React.useEffect(() => {
    if (!location.pathname.startsWith("/dashboard")) return undefined;

    const path = `${location.pathname}${location.search || ""}`;
    const routeProjectKey = projectKeyFromPath(location.pathname);
    const projectMatches = routeProjectKey
      ? [activeProjectSlug, activeProjectId].filter(Boolean).includes(routeProjectKey)
      : false;

    // En rutas de proyecto esperamos a que el contexto resuelva el proyecto.
    // Así no registramos primero por slug y luego otra vez por id.
    if (routeProjectKey && !projectMatches) return undefined;

    const projectId = projectMatches ? activeProjectId : "";
    const projectSlug = projectMatches ? activeProjectSlug : routeProjectKey;
    const visitKey = `${location.key || "default"}:${projectId || projectSlug || "global"}:${path}`;
    let visit = activePanelVisits.get(visitKey);

    if (!visit) {
      const identity = nextPanelActivityStep();
      const common = {
        projectId,
        projectSlug,
        projectName: projectMatches ? activeProjectName : "",
        sessionId: identity.sessionId,
        tabId: identity.tabId,
        step: identity.step,
        path,
        journey: {
          channel: "panel",
          sessionId: identity.sessionId,
          tabId: identity.tabId,
          step: identity.step,
          path,
        },
        metadata: { label: document.title || path },
      };

      visit = {
        key: visitKey,
        common,
        startedAt: Date.now(),
        mounts: 0,
        closeTimer: null,
        closed: false,
      };
      activePanelVisits.set(visitKey, visit);
      activityApi.trackPanel({ action: "panel_route_view", ...common }).catch(() => {});
    } else if (visit.closeTimer) {
      clearTimeout(visit.closeTimer);
      visit.closeTimer = null;
    }

    visit.mounts += 1;
    const handlePageHide = () => closeVisit(visit, true);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      visit.mounts = Math.max(0, visit.mounts - 1);
      if (visit.mounts === 0) closeVisit(visit, false);
    };
  }, [
    location.key,
    location.pathname,
    location.search,
    activeProjectId,
    activeProjectSlug,
    activeProjectName,
  ]);

  return null;
}
